import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, Handshake, ListChecks, Heart, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { KpiTiles, Catalog } from "@/components/PartnerHome";

const PRODUCT_NAMES = {
  rc_economia_solidale: "RC Economia Solidale",
  mrv_volontariato: "Tutela Multirischi del Volontariato",
  infortuni_economia_solidale: "Infortuni per l'Economia Solidale",
  cat_nat_impresa: "CAT NAT Assimoco",
  multirischi_beni: "Tutela Multirischi Danni ai Beni",
  cooperazione_salute: "Cooperazione Salute 4You",
  d_o: "D&O Directors & Officers",
};

export default function Worklist() {
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [converting, setConverting] = useState(null);
  const [summary, setSummary] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadRequests = () => api.get("/requests").then(({ data }) => setRequests(data));

  useEffect(() => {
    loadRequests();
    api.get("/quotes").then(({ data }) => setQuotes(data));
    api.get("/dashboard/partner-summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/quotes/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, []);

  const trasformaRete = async (rid) => {
    setConverting(rid);
    try {
      const { data } = await api.post(`/rete-solidale/requests/${rid}/trasforma`);
      toast.success("Lead trasformato in preventivo Infortuni (Formula Service)");
      navigate(`/preventivi/${data.quote_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(null); }
  };

  const newRequests = requests.filter((r) => r.stato === "richiesta");

  const ALERT_META = [
    { key: "non_aperto", label: "Proposta inviata ma NON aperta dal cliente", hint: "> 2 giorni" },
    { key: "non_accettato", label: "Aperta ma NON ancora accettata", hint: "> 5 giorni" },
    { key: "non_pagato", label: "Accettata ma NON ancora pagata", hint: "> 7 giorni" },
    { key: "polizza_mancante", label: "Pagata ma polizza NON ancora caricata", hint: "> 5 giorni" },
    { key: "proposta_unica", label: "Proposta unica ferma dal cliente", hint: "non aperta > 2 giorni · non confermata > 5 giorni" },
  ];
  const alertBuckets = alerts ? ALERT_META.map((m) => ({ ...m, items: alerts.buckets[m.key] || [] })).filter((b) => b.items.length > 0) : [];

  const buckets = [
    { key: "proposte", label: "Proposte da inviare al cliente", cta: "Apri fascicolo", tone: "terra",
      items: quotes.filter((q) => q.stato === "preventivo_emesso" && !q.inviato_cliente_at) },
    { key: "accettate", label: "Accettate — richiedi emissione", cta: "Richiedi emissione",
      items: quotes.filter((q) => q.stato === "accettato_cliente") },
    { key: "attesa-proposta", label: "In attesa del n. proposta dall'Agenzia",
      items: quotes.filter((q) => q.stato === "in_attesa_bonifico" && !q.numero_proposta) },
    { key: "avviso", label: "Avviso di pagamento da inviare", tone: "terra",
      items: quotes.filter((q) => q.stato === "in_attesa_bonifico" && q.numero_proposta) },
    { key: "contratti", label: "Contratti da far firmare",
      items: quotes.filter((q) => ["pagato", "polizza_emessa"].includes(q.stato) && q.contratto_originale) },
    { key: "provvigioni", label: "Provvigioni da fatturare",
      items: quotes.filter((q) => q.provvigione_conto?.stato === "maturata") },
  ].filter((b) => b.items.length > 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 data-testid="home-greeting" className="font-heading text-4xl font-semibold text-forest">Buongiorno {(user?.name || "").split(" ")[0]}</h1>
        <p className="mt-1 text-muted-foreground">Scegli un settore o riprendi da dove avevi lasciato.</p>
      </div>
      <KpiTiles summary={summary} />
      <Catalog user={user} />
      {user?.rete_solidale_access && (
        <Link to="/rete-gestione" data-testid="home-card-rete-solidale"
          className="group relative block overflow-hidden rounded-2xl bg-forest p-6 text-white transition-transform hover:-translate-y-0.5 md:max-w-[calc(50%-0.5rem)]">
          <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-[#5E9732]" />
          <div className="relative space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Accesso concesso dall'Agenzia</div>
            <div className="flex items-center gap-2 font-heading text-2xl font-semibold"><Handshake className="h-6 w-6" />Rete Solidale</div>
            <p className="max-w-sm text-sm text-white/80">Accordi di Maggior Favore, Convenzioni e Vetrina cooperative, con i poteri dell'Agenzia.</p>
            <span className="inline-flex items-center gap-1 text-sm font-medium">Apri <span className="transition-transform group-hover:translate-x-1">→</span></span>
          </div>
        </Link>
      )}

      <Card className="rounded-xl border-border/60 p-5" data-testid="da-lavorare-oggi">
        <div className="mb-4 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-terracotta" />
          <h2 className="font-heading text-lg font-semibold text-forest">Da lavorare oggi</h2>
        </div>
        {buckets.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Tutto in ordine: nessuna azione in sospeso.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {buckets.map((b) => (
              <div key={b.key} data-testid={`bucket-${b.key}`}
                className={`rounded-xl border p-4 ${b.tone === "terra" ? "border-terracotta/30 bg-terracotta/5" : "border-border/60 bg-sage/30"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-forest">{b.label}</span>
                  <span className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-semibold ${b.tone === "terra" ? "bg-terracotta text-white" : "bg-forest/10 text-forest"}`}>{b.items.length}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {b.items.slice(0, 4).map((q) => (
                    <Link key={q.id} to={`/preventivi/${q.id}`} data-testid={`bucket-item-${q.id}`}
                      className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-white/70">
                      <span className="font-medium text-forest">#{q.id.slice(-6).toUpperCase()}</span>
                      <span className="truncate pl-2 text-xs text-muted-foreground">{PRODUCT_NAMES[q.product_code] || q.product_code}</span>
                    </Link>
                  ))}
                  {b.items.length > 4 && <p className="px-2 pt-1 text-xs text-muted-foreground">+{b.items.length - 4} altri</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Da sollecitare (alert automazioni interne) */}
      {alertBuckets.length > 0 && (
        <Card className="rounded-xl border-amber-200 bg-amber-50/40 p-5" data-testid="da-sollecitare">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading text-lg font-semibold text-forest">Da sollecitare</h2>
            <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-amber-500 px-2 text-xs font-semibold text-white">{alerts.counts.totale}</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {alertBuckets.map((b) => (
              <div key={b.key} data-testid={`alert-${b.key}`} className="rounded-xl border border-amber-200 bg-white/70 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-forest">{b.label}</span>
                  <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-amber-500 px-2 text-xs font-semibold text-white">{b.items.length}</span>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{b.hint}</div>
                <div className="mt-2 space-y-1">
                  {b.items.slice(0, 4).map((q) => (
                    <Link key={q.id} to={`/preventivi/${q.quote_id || q.id}`} data-testid={`alert-item-${q.id}`}
                      className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-amber-50">
                      <span className="shrink-0 font-medium text-forest">{b.key === "proposta_unica" ? `${q.n_prodotti} ${q.n_prodotti === 1 ? "prodotto" : "prodotti"} · ${q.kind === "non_aperta" ? "non aperta" : "non confermata"}` : (q.codice_prev || `#${q.id.slice(-6).toUpperCase()}`)}</span>
                      <span className="truncate pl-2 text-xs text-muted-foreground">{q.giorni}gg · {q.cliente || "—"}</span>
                    </Link>
                  ))}
                  {b.items.length > 4 && <p className="px-2 pt-1 text-xs text-muted-foreground">+{b.items.length - 4} altri</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {newRequests.length > 0 && (
        <Card className="rounded-xl border-border/60 p-5" data-testid="richieste-da-lavorare">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-forest">Richieste da lavorare</h2>
            <span className="text-xs text-muted-foreground">{newRequests.length} da avviare</span>
          </div>
          <div className="space-y-2">
            {newRequests.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessuna richiesta in attesa.</p>}
            {newRequests.slice(0, 6).map((r) => (
              <div key={r.id} data-testid={`request-row-${r.id}`} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-forest">
                    {r.canale === "rete_solidale" && <Heart className="h-3.5 w-3.5 text-terracotta" />}
                    {r.dati_cliente?.ragione_sociale}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.canale === "rete_solidale" ? `Rete Solidale · ${r.convenzione?.codice || "—"}` : `${r.cluster || "—"} · canale ${r.canale}`}
                  </div>
                </div>
                {r.canale === "rete_solidale" ? (
                  <Button size="sm" variant="ghost" data-testid={`worklist-trasforma-rete-${r.id}`} disabled={converting === r.id}
                    onClick={() => trasformaRete(r.id)} className="gap-1 text-terracotta hover:text-terracotta-hover">
                    {converting === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Trasforma <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Link to={`/quota?request=${r.id}`}>
                    <Button size="sm" variant="ghost" className="gap-1 text-terracotta hover:text-terracotta-hover">
                      Quota <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </Card>

      )}
    </div>
  );
}
